/**
 * RecordEndedDialog — Admin deal-level action "Record that the offer ended by
 * a document both parties signed" (copy §15.2, `ADMIN_DEAL_RECORD_ENDED`).
 *
 * Reached from a deal in the "Held contingencies" tab, or by transaction ID
 * (the action is available whether or not any row is held). The admin
 * records only what the document shows and decides nothing: the dialog
 * requires the signed document, a note and the copy's acknowledgement, warns
 * that it ends the offer (and how many rows will be shown as no longer
 * applying), and sends the transaction version the admin saw so the server
 * can refuse a stale record (409 `stale_state`). The server re-checks all of it.
 *
 * Contract: heldContingenciesAdminAgent.getDealEnd / uploadDealEvidence / recordEnded.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { heldContingenciesAdminAgent } from '../../agents/heldContingenciesAdminAgent';
import { contractCopy } from '../ContingencyHub/contract/contractCopy';
import styles from './HeldContingenciesManager.module.css';

const ERROR_KEY_BY_CODE = {
  already_ended: 'WI.admin.deal_end.error.already_ended',
  transaction_terminated: 'WI.admin.deal_end.error.already_ended',
  transaction_completed: 'WI.admin.deal_end.error.already_ended',
  transaction_ended: 'WI.admin.deal_end.error.already_ended',
  termination_pending: 'WI.admin.deal_end.error.already_ended',
  not_tracked: 'WI.admin.deal_end.error.not_tracked',
  version_required: 'WI.admin.deal_end.error.stale',
  not_contract_tracked: 'WI.admin.deal_end.error.not_tracked',
  evidence_required: 'WI.admin.deal_end.error.evidence_required',
  evidence_invalid: 'WI.admin.deal_end.error.evidence_required',
  stale_state: 'WI.admin.deal_end.error.stale',
};

/**
 * The admin-facing message for a refused deal-end record: legal's
 * `WI.admin.deal_end.error.*` for its code (or the `copy_key` the server
 * sent), else the server's message, else `fallback`.
 *
 * @param {Error & {status?: number, detail?: any}} err
 * @param {string} fallback
 * @returns {string}
 */
export function dealEndErrorMessage(err, fallback) {
  if (err?.status === 403) return 'You need admin access to record this.';
  const detail = err?.detail && typeof err.detail === 'object' ? err.detail : null;
  const fromKey = typeof detail?.copy_key === 'string' && detail.copy_key.startsWith('WI.admin.deal_end.error.')
    ? contractCopy(detail.copy_key) : null;
  if (fromKey) return fromKey;
  const key = ERROR_KEY_BY_CODE[detail?.code];
  if (key) return contractCopy(key);
  if (typeof detail?.message === 'string' && detail.message) return detail.message;
  return err?.message || fallback;
}

/**
 * @param {object} props
 * @param {number} props.transactionId
 * @param {object} [props.initial] - What the caller already knows of the deal
 *   (`{property_address, state}`), shown until the server answers.
 * @param {Function} props.onClose
 * @param {Function} props.onDone - `(result, transactionId)` after it was recorded.
 */
export default function RecordEndedDialog({ transactionId, initial = null, onClose, onDone }) {
  const uid = useId();
  const noteRef = useRef(null);
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // A retry after a failed record reuses the upload instead of storing the file twice.
  const [uploaded, setUploaded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    heldContingenciesAdminAgent.getDealEnd(transactionId)
      .then((result) => { if (!cancelled) setDeal(result || {}); })
      .catch((err) => {
        if (!cancelled) setLoadError(dealEndErrorMessage(err, 'The transaction could not be loaded.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [transactionId]);

  const txn = { ...(initial || {}), ...(deal?.transaction || {}) };
  const version = deal?.version ?? null;
  const openCount = Number(deal?.open_count ?? 0);
  const refused = Boolean(deal) && deal.allowed === false;
  const refusalText = refused
    ? dealEndErrorMessage({ status: 409, detail: deal.refusal || {} }, contractCopy('WI.admin.deal_end.error.already_ended'))
    : null;
  const ready = Boolean(deal) && !refused && version != null;
  const signers = Array.isArray(deal?.signers) ? deal.signers : [];

  const noteMissing = note.trim().length < 10;
  const canSubmit = ready && !saving && confirmed && !noteMissing && Boolean(file);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      let evidence = uploaded && uploaded.file === file ? uploaded.data : null;
      if (!evidence) {
        evidence = await heldContingenciesAdminAgent.uploadDealEvidence(transactionId, file);
        setUploaded({ file, data: evidence });
      }
      const result = await heldContingenciesAdminAgent.recordEnded(transactionId, {
        note,
        expectedVersion: version,
        evidenceFileId: evidence.file_id,
      });
      onDone(result, transactionId);
    } catch (err) {
      setError(dealEndErrorMessage(err, 'This could not be recorded. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  const address = txn.property_address || `Transaction #${transactionId}`;

  return (
    <Modal
      open
      onClose={saving ? () => {} : onClose}
      title={contractCopy('WI.admin.deal_end.label')}
      size="md"
      initialFocusRef={noteRef}
      dismissOnOverlayClick={false}
    >
      <form onSubmit={submit} data-testid="deal-end-dialog" noValidate>
        <p className={styles.eyebrow} data-testid="deal-end-address">
          {address}{txn.state ? ` · ${txn.state}` : ''}{` · Transaction #${transactionId}`}
        </p>
        <section className={styles.effect} aria-labelledby={`${uid}-effect`}>
          <h4 id={`${uid}-effect`} className={styles.effectTitle}>When to use this</h4>
          <p className={styles.confirmText} data-testid="deal-end-help">{contractCopy('WI.admin.deal_end.help')}</p>
        </section>

        {loading && <p className={styles.muted} role="status" data-testid="deal-end-loading">Loading the transaction…</p>}
        {loadError && <p className={styles.errorBox} role="alert" data-testid="deal-end-load-error">{loadError}</p>}
        {refusalText && <p className={styles.errorBox} role="alert" data-testid="deal-end-refusal">{refusalText}</p>}

        {ready && signers.length > 0 && (
          <section aria-labelledby={`${uid}-signers`} data-testid="deal-end-signers">
            <h4 id={`${uid}-signers`} className={styles.subheading}>Who signed the offer as Buyer or Seller</h4>
            <ul className={styles.fileList}>
              {signers.map((s) => (
                <li key={`${s.person_id}-${s.role}`}>{s.name || `Person #${s.person_id}`} ({s.role})</li>
              ))}
            </ul>
            <p className={styles.hint}>The document must be signed by each of them.</p>
          </section>
        )}

        {ready && (
          <>
            <p className={styles.errorBox} role="note" data-testid="deal-end-warning">
              {contractCopy('WI.admin.deal_end.warning')}
            </p>
            {openCount > 0 && (
              <p className={styles.errorBox} role="note" data-testid="deal-end-open-rows">
                {openCount === 1
                  ? contractCopy('WI.admin.deal_end.warning.open_rows.one')
                  : contractCopy('WI.admin.deal_end.warning.open_rows', { open_count: openCount })}
              </p>
            )}
          </>
        )}

        <label className={styles.label} htmlFor={`${uid}-file`}>Signed document (required)</label>
        <input
          id={`${uid}-file`}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className={styles.input}
          onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          aria-required="true"
          aria-describedby={`${uid}-file-hint`}
          disabled={!ready}
          data-testid="deal-end-evidence-input"
        />
        <p className={styles.hint} id={`${uid}-file-hint`}>{contractCopy('WI.admin.deal_end.evidence.required')}</p>

        <label className={styles.label} htmlFor={`${uid}-note`}>Note (required)</label>
        <textarea
          id={`${uid}-note`}
          ref={noteRef}
          className={styles.textarea}
          rows={4}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-required="true"
          aria-describedby={`${uid}-note-hint`}
          disabled={!ready}
          data-testid="deal-end-note"
        />
        <p className={styles.hint} id={`${uid}-note-hint`}>{contractCopy('WI.admin.deal_end.note.hint')}</p>

        <label className={styles.confirmCheck} data-testid="deal-end-confirm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={!ready}
          />
          <span>{contractCopy('WI.admin.deal_end.confirm')}</span>
        </label>

        {error && <p className={styles.errorBox} role="alert" data-testid="deal-end-error">{error}</p>}

        <div className={styles.confirmActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.primaryBtn} ${styles.dangerBtn}`}
            disabled={!canSubmit}
            data-testid="deal-end-submit"
          >
            {saving ? 'Recording…' : 'Record that the offer ended'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
